import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ConfirmationService } from 'primeng/api';
import { ProductDetailsResponse, ProductsService, ProductAttributeDefinitionsService, FilterRequest, ProductAttributeDefinitionDetailsMinimalResponse, BrandsService } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { SessionService } from '@app/shared/services/session.service';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';

@Component({
    selector: 'app-view-product',
    templateUrl: './view-product.component.html',
    styleUrl: './view-product.component.scss',
    host: {
        class: 'app-view-product'
    },
    providers: [StatusPipe, ConfirmationService],
    standalone: false
})
export class ViewProductComponent extends BaseViewComponent implements OnInit, OnDestroy {
  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  brandsTotalRecords: number = 0;
  hasOnlyOneActiveBrand: boolean = false;

  productAttributesFeatureEnabled: boolean = false;
  showProductAttributeDetails: boolean = false;

  canViewPrice: boolean = false;
  delete: boolean = false;
  productId: number = 0;
  product: ProductDetailsResponse = {
    id: 0,
    name: '',
    sku: '',
    description: '',
    isCustomisable: false,
    isActive: false,
    isLimitedEdition: false,
    isSerialNumberRequired: false,
    warrantyDurationMonths: 0,
    productType: {
      id: 0,
      name: '',
      code: ''
    }
  };
  allowModifyProduct: boolean = false;
  stateOptions: any[];
  stateInitialValue: boolean = false;
  stateCurrentValue: boolean = false;
  stateNewValue = false;
  sanitizedConfirmMessage: SafeHtml | undefined;
  productAttributeDefinitions: ProductAttributeDefinitionDetailsMinimalResponse[] = [];
  translatedProductAttributes: { code: string, name: string; value: string, changesOverTime: boolean }[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private session: SessionService,
    private productsService: ProductsService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private brandsService: BrandsService,
    private permissionService: PermissionService,
    private confirmationService: ConfirmationService) {
    super();
    this.updateStateOptions();
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
      });

    this.init();
  }

  private async init(): Promise<void> {

    const companySettings = await this.session.getCompanySettings();
    this.productAttributesFeatureEnabled = companySettings.features?.some(f => f.name === 'ProductAttributes' && f.isEnabled) ?? false;

    // i18n
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.buildConfirmMessage(this.stateCurrentValue);
        this.cdr.detectChanges();
        this.fillTranslatedAttributes();
      });

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async (params) => {
        this.productId = Number(params['productId']);
        if (this.productId > 0) {
          await Promise.all([
            this.getProductAttributesDefinitions(),
            this.getBrands()
          ]);

          await this.get();
        }
      });

    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.showProductAttributeDetails = company?.showProductAttributeDetails ?? false;
        this.canViewPrice = company?.hasPrice ?? false;
        this.cdr.detectChanges();
      });

    this.allowModifyProduct = await this.permissionService.hasActionPermissions(Actions.modify_products);
    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
  }

  private async getBrands(): Promise<void> {
    const payload = {
      pageNumber: 1,
      pageSize: 1,
      additionalFilters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    const brandsPage = await firstValueFrom(this.brandsService.apiBrandsFilterComboPost(payload));

    this.brandsTotalRecords = brandsPage.totalCount ?? 0;
    this.hasOnlyOneActiveBrand = this.brandsTotalRecords === 1;
  }

  private syncStateFromProduct() {
    this.stateInitialValue = !!this.product?.isActive;
    this.stateCurrentValue = this.stateInitialValue;
    this.buildConfirmMessage(this.stateCurrentValue);
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch() {
    this.stateInitialValue = this.product.isActive ?? false;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateInitialValue)?.label.toLowerCase();
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  private buildConfirmMessage(value: boolean) {
    const selectedLabel = this.stateOptions.find(o => o.value === value)?.label?.toLowerCase() ?? '';
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  onStateChange(event: any) {
    this.stateNewValue = event.value;
    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  onConfirmStateChange() {
    if (!this.stateNewValue) {
      this.productsService.apiProductsIdDeactivatePut(this.productId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to deactivate product', error);
        }
      });
    } else if (this.stateNewValue) {
      this.productsService.apiProductsIdActivatePut(this.productId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to activate product', error);
        }
      });
    }
    this.confirmationService.close();
  }

  onCancelStateChange() {
    // Revert state change
    this.stateCurrentValue = this.stateInitialValue;
    this.confirmationService.close();
    this.cdr.detectChanges();
  }

  fillTranslatedAttributes(){
    this.translatedProductAttributes = this.productAttributeDefinitions?.map(def => {
      const translatedName = def.productAttributeDefinitionTranslations?.find(t => t.isoCode === this.translate.currentLang)?.translatedName
        ?? (def.productAttributeDefinitionTranslations?.find(t => t.isoCode === 'en')?.translatedName ?? def.definitionCode);
      const productAttributeValues = this.product.productAttributeValues?.filter(attrValue => attrValue.productAttributeDefinitionId === def.id);
      let translatedValue = "-";
      if (productAttributeValues) {
        const translatedValues = productAttributeValues.map(productAttributeValue =>
          productAttributeValue.productAttributeValueTranslations?.find(t => t.isoCode === this.translate.currentLang)?.translatedValue
            ?? (productAttributeValue.productAttributeValueTranslations?.find(t => t.isoCode === 'en')?.translatedValue ?? productAttributeValue.valueCode));
        translatedValue = translatedValues.join(", ");
      }
      return { code: def.definitionCode, name: translatedName, value: translatedValue, changesOverTime: def.changesOverTime };
    }) ?? [];
  }

  override async get() {
    try {
      this.product = await firstValueFrom(this.productsService.apiProductsDetailsIdGet(this.productId));
      this.fillTranslatedAttributes();
      this.syncStateFromProduct();
    } catch (error) {
      console.log('Error fetching product data:', error);
    } finally {
      this.cdr.detectChanges();
    }
  }

  async getProductAttributesDefinitions() {
    if (!this.productAttributesFeatureEnabled) {
      this.productAttributeDefinitions = [];
      this.translatedProductAttributes = [];
      return;
    }

    try {
      const filter: FilterRequest = {
        pageSize: 500,
        pageNumber: 1,
        filters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
      };

      this.productAttributeDefinitions = (await firstValueFrom(this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterDefinitionsOnlyPost(filter))).results ?? [];

    } catch (error) {
      console.log('Error fetching product attributes definition data:', error);
    }
  }

  goToEditProduct() {
    this.router.navigate([`/products/${this.productId}/edit`]);
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.productsService.apiProductsDeleteCheckBatchPost([this.productId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteProduct(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.productsService.apiProductsDeleteIdDelete(this.productId));
      this.router.navigate([`/products`]);
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
